import { MouseEventHandler, RefObject, useCallback, useRef, useState } from 'react'

/**
 * This hook solves a problem that has been around since the dawn of the web
 * Yes, animating height!
 *
 * It's possible to animate height with an exact value but not with percentages
 * You could animate with max-height: 100% and hope that the content would never exceed the container ...
 * ... but that would be silly!
 *
 * Instead a ref is used to get the element's bounding box ...
 * ... and uses it to calculate the supposed height
 *
 * This is an example of using it in a React component
 * <div> style={{ height, transition: `height ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 1, 1)`}}>... </div>
 * See Accordion.tsx as an example
 */

type UseAnimateHeight<T extends HTMLElement> = {
  height: number
  handleClick: MouseEventHandler<HTMLButtonElement>
  contentRef: RefObject<T>
  isOpen: boolean
  updateHeight: () => void
}

const useAnimateHeight = <T extends HTMLElement>(): UseAnimateHeight<T> => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const contentRef = useRef<T>(null)
  const [height, setHeight] = useState<number>(0)

  const updateHeight = useCallback(() => {
    if (!contentRef.current) return

    const contentRect = contentRef.current.getBoundingClientRect()
    setHeight(contentRect.height)
  }, [])

  const handleClick = useCallback((): void => {
    if (!contentRef.current) return

    setIsOpen((prev) => !prev)
  }, [])

  return { height, handleClick, contentRef, isOpen, updateHeight }
}

export default useAnimateHeight
